import React, { useState, useMemo, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, dateInRange, uuid, isTaskOverdue } from '../../core/helpers';
import { ORG_JSON_KEYS, persistOrgJsonSetting } from '../../core/org-json-settings';
import { downloadExcelBlob, printReportPdf } from '../../core/report-export';
import { FilterBar } from '../../components/FilterBar';
import { Icon, InlineIcon } from '../../components/Icon';
import { DrillPanel } from '../dashboard/AdminDashboard';

const SOURCE_META = {
  clients: { icon: "company", file: "founders_bridge_clients.xlsx", label: "Clients" },
  invoices: { icon: "doc", file: "founders_bridge_invoices.xlsx", label: "Invoices" },
  tasks: { icon: "checkCircle", file: "founders_bridge_tasks.xlsx", label: "Tasks" },
  recurring_tasks: { icon: "repeat", file: "founders_bridge_recurring_tasks.xlsx", label: "Recurring Tasks" },
  employee_wise: { icon: "employees", file: "founders_bridge_employee_wise.xlsx", label: "Employee-wise" },
  payments: { icon: "money", file: "founders_bridge_payments.xlsx", label: "Payments" },
};

const BUILTIN_IDS = new Set(Object.keys(SOURCE_META));

function loadSavedViews(org, userId) {
  const remote = (Array.isArray(org?.savedReportViews) ? org.savedReportViews : [])
    .filter(v => !v.userId || v.userId === userId);
  if (remote.length) return remote;
  try { return JSON.parse(localStorage.getItem("fb_saved_report_views") || "[]"); } catch { return []; }
}

async function persistCustomReports(reports, setOrg) {
  await persistOrgJsonSetting(ORG_JSON_KEYS.customReports, reports, setOrg, 'customReports');
}

function mergeFilters(page, report = {}) {
  return {
    from: page.from || report.from || "",
    to: page.to || report.to || "",
    clientId: page.clientId || report.clientId || "",
    employeeId: page.employeeId || report.employeeId || "",
  };
}

function ReportsPage({ clients, invoices, tasks, employees, payments }) {
  const { user, org, setOrg, showToast } = useApp();
  const isAdmin = user?.role === "admin";
  const customReports = useMemo(
    () => {
      const all = Array.isArray(org?.customReports) ? org.customReports : [];
      if (isAdmin) return all;
      return all.filter(r => !r.createdById || r.createdById === user?.id);
    },
    [org?.customReports, isAdmin, user?.id]
  );

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [clientId, setClientId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [drill, setDrill] = useState(null);
  const [downloadPrompt, setDownloadPrompt] = useState(null); // { report, format: 'excel'|'pdf' }
  const [savedViews, setSavedViews] = useState(() => loadSavedViews(org, user?.id));
  const [viewName, setViewName] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({
    title: "", source: "clients", desc: "", icon: "company", includeFilters: true,
  });

  const filterData = useCallback((filters) => {
    const fClients = clients.filter(c => {
      if (filters.clientId && c.id !== filters.clientId) return false;
      if (filters.employeeId && c.assignedTo !== filters.employeeId) return false;
      return true;
    });
    const fInvoices = invoices.filter(i => {
      if (filters.clientId && i.clientId !== filters.clientId) return false;
      if (filters.employeeId) {
        const cl = clients.find(c => c.id === i.clientId);
        if (cl?.assignedTo !== filters.employeeId) return false;
      }
      if (!dateInRange(i.date, filters.from, filters.to)) return false;
      return true;
    });
    const fTasks = tasks.filter(t => {
      if (filters.clientId && t.clientId !== filters.clientId) return false;
      if (filters.employeeId && t.assignedTo !== filters.employeeId) return false;
      if (!dateInRange(t.dueDate || t.completedDate, filters.from, filters.to)) return false;
      return true;
    });
    const fPayments = (payments || []).filter(p => {
      if (filters.clientId && p.clientId !== filters.clientId) return false;
      if (!dateInRange(p.date, filters.from, filters.to)) return false;
      return true;
    });
    const fRecurringTasks = tasks.filter(t => {
      if (!t.isRecurring) return false;
      if (filters.clientId && t.clientId !== filters.clientId) return false;
      if (filters.employeeId && t.assignedTo !== filters.employeeId) return false;
      if (!dateInRange(t.dueDate || t.startDate, filters.from, filters.to)) return false;
      return true;
    });
    return { fClients, fInvoices, fTasks, fPayments, fRecurringTasks };
  }, [clients, invoices, tasks, payments]);

  const pageFilters = useMemo(() => ({ from, to, clientId, employeeId }), [from, to, clientId, employeeId]);
  const { fClients, fInvoices, fTasks, fPayments, fRecurringTasks } = filterData(pageFilters);

  const totalBilling = fClients.reduce((s, c) => s + (c.totalBilling || 0), 0);
  const totalCollected = fInvoices.reduce((s, i) => s + (i.paid || 0), 0);
  const totalPending = fInvoices.reduce((s, i) => s + (i.pending || 0), 0);

  const downloadExcel = (data, filename) => {
    if (!data.length) { showToast("No rows to export", "error"); return; }
    if (!downloadExcelBlob(data, filename)) showToast("Could not export Excel file", "error");
  };

  const downloadPDF = (data, title) => {
    if (!data.length) { showToast("No rows to export", "error"); return; }
    if (!printReportPdf(data, title)) showToast("Allow pop-ups to export PDF", "error");
  };

  const empName = (id) => employees.find(e => e.id === id)?.name || "";

  const staffEmployees = useMemo(
    () => (employees || []).filter(e => e.role === "employee" || e.role === "manager"),
    [employees],
  );

  const buildEmployeeWiseRows = useCallback((filters, scopedClients, scopedTasks) => {
    let emps = staffEmployees;
    if (filters.employeeId) emps = emps.filter(e => e.id === filters.employeeId);
    return emps.map(emp => {
      const empClients = scopedClients.filter(c => c.assignedTo === emp.id);
      const empTasks = scopedTasks.filter(t => {
        if (t.assignedTo !== emp.id) return false;
        if (filters.from || filters.to) {
          return dateInRange(t.dueDate || t.completedDate || t.startDate, filters.from, filters.to);
        }
        return true;
      });
      const open = empTasks.filter(t => t.status !== "completed" && t.status !== "cancelled").length;
      const completed = empTasks.filter(t => t.status === "completed").length;
      const overdue = empTasks.filter(t => isTaskOverdue(t)).length;
      const billing = empClients.reduce((s, c) => s + (c.totalBilling || 0), 0);
      const collected = empClients.reduce((s, c) => s + (c.collected || 0), 0);
      const pending = empClients.reduce((s, c) => s + (c.pending || 0), 0);
      return {
        Employee: emp.name,
        Email: emp.email || "",
        Role: emp.role || "",
        "Clients Assigned": empClients.length,
        "Open Tasks": open,
        Completed: completed,
        Overdue: overdue,
        "Total Billing (INR)": billing,
        "Collected (INR)": collected,
        "Pending (INR)": pending,
      };
    });
  }, [staffEmployees]);

  const buildRows = useCallback((source, filters, filteredOnly) => {
    const { fClients: fc, fInvoices: fi, fTasks: ft, fPayments: fp, fRecurringTasks: fr } = filterData(filters);
    const recurringRow = (t) => ({
      Package: t.packageId || t.recurringTemplate || "—",
      Task: t.title,
      Client: t.clientName,
      Category: t.category || "",
      Frequency: t.freq || "",
      "Due Date": t.dueDate || "",
      Status: t.status,
      "Period Start": t.startDate || "",
      "Period End": t.endDate || "",
      "Assigned To": empName(t.assignedTo),
    });
    const unfiltered = {
      clients: clients.map(c => ({ "Client No": c.clientNo, Name: c.name, "Total Billing (INR)": c.totalBilling })),
      invoices: invoices.map(i => ({ "Invoice No": i.invoiceNo, Client: i.clientName, "Total (INR)": i.total })),
      tasks: tasks.map(t => ({ Task: t.title, Client: t.clientName, Status: t.status })),
      recurring_tasks: tasks.filter(t => t.isRecurring).map(recurringRow),
      employee_wise: buildEmployeeWiseRows({}, clients, tasks),
      payments: (payments || []).map(p => ({ Date: p.date, Client: p.clientName, "Amount (INR)": p.amount })),
    };
    const filtered = {
      clients: fc.map(c => ({
        "Client No": c.clientNo, Name: c.name, Type: c.type, Contact: c.contactName,
        Phone: c.phone, "Total Billing (INR)": c.totalBilling, "Collected (INR)": c.collected,
        "Pending (INR)": c.pending, "Progress %": c.progress, Status: c.status, Created: c.createdAt,
      })),
      invoices: fi.map(i => ({
        "Invoice No": i.invoiceNo, Client: i.clientName, Date: i.date,
        "Total (INR)": i.total, "Paid (INR)": i.paid, "Pending (INR)": i.pending, Status: i.status,
      })),
      tasks: ft.map(t => ({
        Task: t.title, Client: t.clientName, Status: t.status, "Due Date": t.dueDate || "",
        Completed: t.completedDate || "", Sequence: t.sequence,
      })),
      recurring_tasks: fr.map(recurringRow),
      employee_wise: buildEmployeeWiseRows(filters, fc, ft),
      payments: fp.map(p => ({
        Date: p.date, Client: p.clientName, Invoice: p.invoiceId,
        "Amount (INR)": p.amount, Mode: p.mode, Reference: p.reference, Notes: p.notes,
      })),
    };
    return filteredOnly ? filtered[source] : unfiltered[source];
  }, [clients, invoices, tasks, payments, filterData, employees, buildEmployeeWiseRows]);

  const countForSource = useCallback((source, filters) => {
    if (source === "employee_wise") {
      return filters.employeeId ? 1 : staffEmployees.length;
    }
    const { fClients: fc, fInvoices: fi, fTasks: ft, fPayments: fp, fRecurringTasks: fr } = filterData(filters);
    return { clients: fc.length, invoices: fi.length, tasks: ft.length, recurring_tasks: fr.length, payments: fp.length }[source] ?? 0;
  }, [filterData, staffEmployees]);

  const builtinReports = useMemo(() => Object.entries(SOURCE_META).map(([id, meta]) => ({
    id,
    source: id,
    builtin: true,
    icon: meta.icon,
    title: `${meta.label} Report`,
    desc: `${countForSource(id, pageFilters)} ${meta.label.toLowerCase()}`,
    file: meta.file,
  })), [countForSource, pageFilters]);

  const allReports = useMemo(() => [
    ...builtinReports,
    ...customReports.map(r => ({
      ...r,
      builtin: false,
      desc: r.desc || `${countForSource(r.source, mergeFilters(pageFilters, r))} ${SOURCE_META[r.source]?.label?.toLowerCase() || "rows"}`,
      file: r.file || `founders_bridge_${(r.title || "report").replace(/\s+/g, "_").toLowerCase()}.xlsx`,
    })),
  ], [builtinReports, customReports, countForSource, pageFilters]);

  const askDownload = (report, format) => setDownloadPrompt({ report, format });
  const confirmDownload = (useFilters) => {
    const { report: r, format } = downloadPrompt || {};
    setDownloadPrompt(null);
    if (!r) return;
    const source = r.source || r.id;
    const filters = useFilters ? mergeFilters(pageFilters, r) : {};
    const hasFilters = useFilters && (filters.from || filters.to || filters.clientId || filters.employeeId);
    const data = buildRows(source, filters, useFilters);
    const suffix = hasFilters ? "_filtered" : "";
    if (format === "pdf") {
      downloadPDF(data, r.title);
    } else {
      downloadExcel(data, r.file.replace(/\.(csv|xlsx)$/i, `${suffix}.xlsx`));
    }
  };

  const saveCurrentView = async () => {
    if (!viewName.trim()) return;
    const item = { id: Date.now().toString(), name: viewName.trim(), userId: user?.id, from, to, clientId, employeeId };
    const next = [...savedViews.filter(v => v.name !== item.name), item];
    setSavedViews(next);
    try {
      const all = [...(org?.savedReportViews || []).filter(v => v.userId !== user?.id), ...next];
      await persistOrgJsonSetting(ORG_JSON_KEYS.savedReportViews, all, setOrg, 'savedReportViews');
      showToast("Filter view saved", "success");
    } catch {
      localStorage.setItem("fb_saved_report_views", JSON.stringify(next));
      showToast("Filter view saved locally", "success");
    }
    setViewName("");
  };

  const applyView = (v) => { setFrom(v.from || ""); setTo(v.to || ""); setClientId(v.clientId || ""); setEmployeeId(v.employeeId || ""); };

  const saveNewReport = async () => {
    if (!draft.title.trim()) { showToast("Report name required", "error"); return; }
    if (!SOURCE_META[draft.source]) { showToast("Pick a data source", "error"); return; }
    const item = {
      id: "cr_" + uuid().slice(0, 8),
      title: draft.title.trim(),
      source: draft.source,
      desc: draft.desc.trim(),
      icon: draft.icon || SOURCE_META[draft.source].icon,
      file: `founders_bridge_${draft.title.trim().replace(/\s+/g, "_").toLowerCase()}.xlsx`,
      createdAt: new Date().toISOString(),
      createdBy: user?.name || "Admin",
      createdById: user?.id,
      ...(draft.includeFilters ? { from, to, clientId, employeeId } : {}),
    };
    const next = [...customReports, item];
    try {
      await persistCustomReports(next, setOrg);
      showToast(`Report "${item.title}" created`, "success");
      setShowCreate(false);
      setDraft({ title: "", source: "clients", desc: "", icon: "company", includeFilters: true });
    } catch {
      showToast("Could not save report", "error");
    }
  };

  const removeReport = async (report) => {
    if (report.builtin || BUILTIN_IDS.has(report.id)) return;
    const next = customReports.filter(r => r.id !== report.id);
    try {
      await persistCustomReports(next, setOrg);
      showToast("Report removed", "success");
    } catch {
      showToast("Could not remove report", "error");
    }
  };

  return (
    <>
      <FilterBar from={from} setFrom={setFrom} to={to} setTo={setTo} clientId={clientId} setClientId={setClientId} clients={clients}
        employeeId={employeeId} setEmployeeId={setEmployeeId} employees={employees}
        onClear={() => { setFrom(""); setTo(""); setClientId(""); setEmployeeId(""); }} />

      {isAdmin && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <button className="btn btn-sm btn-primary" type="button" onClick={() => setShowCreate(true)}>
            <InlineIcon name="add" size={14} gap={5}>Create report</InlineIcon>
          </button>
          <input className="f-input" style={{ width: 200 }} placeholder="Save filter view as…" value={viewName} onChange={e => setViewName(e.target.value)} />
          <button className="btn btn-sm" type="button" onClick={saveCurrentView}>Save filter view</button>
          {savedViews.map(v => (
            <button key={v.id} className="btn btn-sm" type="button" onClick={() => applyView(v)}>{v.name}</button>
          ))}
        </div>
      )}

      <div className="stat-cards" style={{ marginBottom: 24 }}>
        {[
          ["Total Clients", fClients.length, "var(--blue)", "clients"],
          ["Total Billing", INR(totalBilling), "var(--ink)", "billing"],
          ["Collected", INR(totalCollected), "var(--green)", "collected"],
          ["Outstanding", INR(totalPending), "var(--red)", "pending"],
        ].map(([l, v, c, drillType]) => (
          <div key={l} className="stat-box" style={{ cursor: "pointer" }} onClick={() => setDrill({ type: drillType, title: l })}>
            <div className="stat-lbl">{l}</div>
            <div className="stat-val" style={{ fontSize: 20, color: c }}>{v}</div>
            <div className="stat-drill">Click to expand →</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 16 }}>
        {allReports.map(r => (
          <div key={r.id} className="card">
            <div className="card-body">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}><Icon name={r.icon} size={28} /></div>
                {!r.builtin && isAdmin && (
                  <button type="button" className="btn btn-sm" style={{ color: "var(--red)", background: "#FEF2F2" }} onClick={() => removeReport(r)}>Remove</button>
                )}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{r.title}</div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>
                Source: {SOURCE_META[r.source]?.label || r.source}
                {!r.builtin && r.createdBy ? ` · by ${r.createdBy}` : ""}
              </div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 16 }}>{r.desc}</div>
              <div style={{ display: "flex", gap: 8, flexDirection: "column" }}>
                <button className="btn btn-primary" style={{ width: "100%" }} type="button" onClick={() => askDownload(r, "excel")}>
                  <InlineIcon name="download" size={14} gap={5}>Export Excel</InlineIcon>
                </button>
                <button className="btn" style={{ width: "100%" }} type="button" onClick={() => askDownload(r, "pdf")}>
                  <InlineIcon name="doc" size={14} gap={5}>Export PDF</InlineIcon>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {drill && <DrillPanel type={drill.type} title={drill.title} onClose={() => setDrill(null)} />}

      {downloadPrompt && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDownloadPrompt(null)}>
          <div className="modal-box" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Export {downloadPrompt.report?.title}</div>
              <button type="button" className="modal-close" onClick={() => setDownloadPrompt(null)}><Icon name="close" size={16} /></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: "var(--muted)", margin: 0, lineHeight: 1.5 }}>
                {downloadPrompt.format === "pdf"
                  ? "Opens a print dialog — choose Save as PDF. Apply page filters to the export?"
                  : "Apply page filters (date, client, employee) to this Excel export?"}
              </p>
            </div>
            <div className="modal-foot">
              <button className="btn" type="button" onClick={() => confirmDownload(false)}>
                All data{downloadPrompt.format === "pdf" ? " → PDF" : ""}
              </button>
              <button className="btn btn-primary" type="button" onClick={() => confirmDownload(true)}>
                Filtered only{downloadPrompt.format === "pdf" ? " → PDF" : ""}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowCreate(false)}>
          <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Create new report</div>
              <button type="button" className="modal-close" onClick={() => setShowCreate(false)}><Icon name="close" size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="f-group">
                  <label className="f-label">Report name <span className="f-req">*</span></label>
                  <input className="f-input" value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} placeholder="e.g. Q1 GST Clients" />
                </div>
                <div className="f-group">
                  <label className="f-label">Data source <span className="f-req">*</span></label>
                  <select className="f-select" value={draft.source} onChange={e => setDraft(d => ({ ...d, source: e.target.value, icon: SOURCE_META[e.target.value]?.icon || d.icon }))}>
                    {Object.entries(SOURCE_META).map(([id, m]) => <option key={id} value={id}>{m.label}</option>)}
                  </select>
                </div>
                <div className="f-group">
                  <label className="f-label">Description</label>
                  <input className="f-input" value={draft.desc} onChange={e => setDraft(d => ({ ...d, desc: e.target.value }))} placeholder="What this report is for" />
                </div>
                <div className="f-group">
                  <label className="f-check" style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, cursor: "pointer", color: "var(--ink)" }}>
                    <input type="checkbox" checked={draft.includeFilters} onChange={e => setDraft(d => ({ ...d, includeFilters: e.target.checked }))} style={{ marginTop: 3 }} />
                    <span>Save current filters (date, client, employee) with this report</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn" type="button" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="btn btn-primary" type="button" onClick={saveNewReport}>Create report</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export { ReportsPage };
